import { Link, useNavigate } from "react-router-dom";
import { Phone, Mail, ArrowUpRight, Facebook, Instagram } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSectionNav } from "@/lib/useSectionNav";
import { drywallServices, r3Services } from "@/data/content";
import {
  EMAIL,
} from "@/lib/site";
import { useBusinessInfo } from "@/lib/useBusinessInfo";

const NAV_LINKS: { key: string; hash?: string; path?: string }[] = [
  { key: "services", hash: "#services" },
  { key: "work", hash: "#work" },
  { key: "builders", hash: "#builders" },
  { key: "r3", hash: "#r3" },
  { key: "about", hash: "#about" },
  { key: "faq", hash: "#faq" },
  { key: "reviews", path: "/reviews" },
  { key: "contact", hash: "#contact" },
];

export const Footer = () => {
  const { t } = useLanguage();
  const biz = useBusinessInfo();
  const goTo = useSectionNav();
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#070707]">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-28">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="font-display text-3xl font-black uppercase leading-none tracking-tight text-white md:text-4xl">
              R3Stan
              <br />
              <span className="text-gold">Drywall, Inc.</span>
            </p>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-neutral-400">
              {t.footer.tagline}
            </p>
            <Link
              data-testid="footer-bid-link"
              to="/request-a-bid"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark"
            >
              {t.nav.requestBid}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="md:col-span-2">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-neutral-500">
              {t.footer.servicesCol}
            </p>
            <ul className="mt-5 space-y-2.5">
              {drywallServices.map((s) => (
                <li key={s.slug} className="text-sm text-neutral-400">
                  {t.services.items[s.slug].title}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-neutral-500">
              {t.footer.r3Col}
            </p>
            <ul className="mt-5 space-y-2.5">
              {r3Services.map((s) => (
                <li key={s.slug} className="text-sm text-neutral-400">
                  {t.r3.items[s.slug].title}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-neutral-500">
              {t.footer.navCol}
            </p>
            <ul className="mt-5 space-y-2.5">
              {NAV_LINKS.map((l) => (
                <li key={l.key}>
                  <button
                    data-testid={`footer-nav-${l.key}`}
                    onClick={() => (l.path ? navigate(l.path) : goTo(l.hash!))}
                    className="text-sm text-neutral-400 transition-colors duration-300 hover:text-gold"
                  >
                    {(t.nav as Record<string, string>)[l.key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-neutral-500">
              {t.footer.contactCol}
            </p>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  data-testid="footer-phone-link"
                  href={biz.phoneHref}
                  className="flex items-center gap-2 text-sm text-neutral-300 transition-colors duration-300 hover:text-gold"
                >
                  <Phone className="h-3.5 w-3.5 text-gold" />
                  {biz.phone}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-email-link"
                  href={biz.emailHref}
                  className="flex items-center gap-2 text-sm text-neutral-300 transition-colors duration-300 hover:text-gold"
                >
                  <Mail className="h-3.5 w-3.5 text-gold" />
                  {biz.email}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-phone2-link"
                  href={biz.phone2Href}
                  className="flex items-center gap-2 text-sm text-neutral-300 transition-colors duration-300 hover:text-gold"
                >
                  <Phone className="h-3.5 w-3.5 text-gold" />
                  {biz.phone2}
                  <span className="text-xs text-neutral-500">· R3 Services</span>
                </a>
              </li>
              <li className="text-sm font-bold text-gold">{t.footer.seHabla}</li>
              <li className="flex items-center gap-3 pt-1">
                <span
                  data-testid="social-facebook"
                  aria-disabled="true"
                  title={t.social.comingSoon}
                  className="flex h-9 w-9 cursor-not-allowed items-center justify-center rounded-full border border-white/10 text-neutral-600"
                >
                  <Facebook className="h-4 w-4" />
                </span>
                <span
                  data-testid="social-instagram"
                  aria-disabled="true"
                  title={t.social.comingSoon}
                  className="flex h-9 w-9 cursor-not-allowed items-center justify-center rounded-full border border-white/10 text-neutral-600"
                >
                  <Instagram className="h-4 w-4" />
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">
                  {t.social.comingSoon}
                </span>
              </li>
            </ul>
            <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.25em] text-neutral-500">
              {t.footer.areaCol}
            </p>
            <p className="mt-3 text-sm text-neutral-400">{t.footer.areaText}</p>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-3 border-t border-white/10 pt-8 text-xs text-neutral-500 md:flex-row md:items-center md:justify-between">
          <p data-testid="footer-rights">
            © {year} {t.footer.rights}
          </p>
          <p>Houston, TX · {t.hero.tagline}</p>
        </div>
      </div>
    </footer>
  );
};
